import {
  IAccountingPeriodLock,
  IRevenueSchedulePeriod,
  IRevenueScheduleSubscriptionItem,
  RevenueScheduleSourceTypeEnum,
} from '@monetize/types/app';
import { formatCurrency } from '@monetize/utils/core';
import {
  AppCellContext,
  AppColumnDef,
  createColumnHelper,
} from '../Monetize/DataTableNew/appTable';
import { addMonths } from 'date-fns/addMonths';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { startOfMonth } from 'date-fns/startOfMonth';
import {
  FC,
  MutableRefObject,
  ReactNode,
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MdError, MdLock } from 'react-icons/md';
import {
  buildServicePeriodIndex,
  formatProductType,
  formatRecognitionMethod,
  formatServicePeriod,
  getPeriodGroupKey,
  isPeriodRecognized,
  ServicePeriodRange,
  withinDecimalPrecision,
} from '../../utils/revenueSchedule';
import {
  MBox,
  MCustomNumberInput,
  MFlex,
  MIcon,
  MSkeleton,
  MText,
  MTooltip,
} from '../Monetize';
import { DataTable } from '../Monetize/DataTableNew/DataTable';
import { OfferingLookupPopover } from '../Monetize/LookupPopover/OfferingLookupPopover';
import { ProductLookupPopover } from '../Monetize/LookupPopover/ProductLookupPopover';
import { PeriodAuditPopover } from './PeriodAuditPopover';

interface RevenueScheduleTableProps {
  subscriptionItems: IRevenueScheduleSubscriptionItem[];
  periods: IRevenueSchedulePeriod[];
  contractStart?: string | null;
  contractEndInclusive?: string | null;
  loadedThrough?: string | null;
  isEditable?: boolean;
  contractAmount?: number | null;
  activeLock?: IAccountingPeriodLock | null;
  isLoadingMore?: boolean;
  onChangeEditedRevenues?: (edits: Record<string, number>) => void;
  onLoadMore?: () => void;
}

const PINNED_LEFT_COLUMNS = ['offeringName', 'productName'];

const PINNED_RIGHT_COLUMNS = ['productAmount'];

interface MonthColumn {
  id: string;
  monthStart: string;
  monthLabel: string;
}

interface RowData {
  rowId: string;
  periodKey: string;
  offeringId: string;
  offeringName: string;
  productId: string;
  productName: string;
  productType: string;
  recognitionMethod: string;
  recognitionDuration?: number | null;
  serviceStart: string;
  serviceEnd: string;
  productAmount: number;
  isTotalRow?: boolean;
}

const TOTAL_ROW_ID = '__total__';
const TCV_MISMATCH_MESSAGE =
  'The total revenue amount must equal the total contract value.';
const LOCKED_PERIOD_MESSAGE =
  'This period has already been recognized and can no longer be edited.';

const REVENUE_INPUT_PRECISION = 2;

const limitRevenueInputDecimals = (val: string): boolean =>
  withinDecimalPrecision(val, REVENUE_INPUT_PRECISION);
const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const MONTH_KEY_FORMAT = 'yyyy-MM';

const COLUMN_WIDTH = 140;
const NARROW_COLUMN_WIDTH = 120;

const COLUMN_DIVIDER_STYLES = {
  borderLeft: '1px solid var(--chakra-colors-gray-300)',
};

const MONTH_CELL_STYLES = {
  ...COLUMN_DIVIDER_STYLES,
  position: 'relative' as const,
};
const AMOUNT_CELL_STYLES = COLUMN_DIVIDER_STYLES;

const columnHelper = createColumnHelper<RowData>();

const LOCKED_PERIOD_TOOLTIP = 'This revenue period has been locked.';

const isPeriodColumnLocked = (
  periodStart: string,
  activeLock: IAccountingPeriodLock | null | undefined,
): boolean => {
  if (!activeLock) {
    return false;
  }
  const lockYearMonth = `${activeLock.year}-${String(activeLock.month).padStart(2, '0')}`;
  return format(parseISO(periodStart), 'yyyy-MM') <= lockYearMonth;
};

const filterLockedEdits = (
  editedRevenues: Record<string, number>,
  periods: IRevenueSchedulePeriod[],
  activeLock: IAccountingPeriodLock,
): Record<string, number> => {
  const periodStartById = new Map(periods.map((p) => [p.id, p.period.start]));
  const filtered: Record<string, number> = {};
  for (const [periodId, value] of Object.entries(editedRevenues)) {
    const start = periodStartById.get(periodId);
    if (!start || !isPeriodColumnLocked(start, activeLock)) {
      filtered[periodId] = value;
    }
  }
  return filtered;
};

const toMonthKey = (iso: string) => format(parseISO(iso), MONTH_KEY_FORMAT);

const buildMonthColumnsFromContract = (
  contractStart: string,
  contractEndInclusive: string,
): MonthColumn[] => {
  const start = startOfMonth(parseISO(contractStart));
  const endMonthStart = startOfMonth(parseISO(contractEndInclusive));
  if (start > endMonthStart) {
    return [];
  }
  const months: MonthColumn[] = [];
  let cursor = start;
  while (cursor <= endMonthStart) {
    const monthStart = format(cursor, ISO_DATE_FORMAT);
    months.push({
      id: format(cursor, MONTH_KEY_FORMAT),
      monthStart,
      monthLabel: format(cursor, MONTH_KEY_FORMAT),
    });
    cursor = addMonths(cursor, 1);
  }
  return months;
};

const buildMonthColumnsFromPeriods = (
  periods: IRevenueSchedulePeriod[],
): MonthColumn[] => {
  const seen = new Map<string, MonthColumn>();
  for (const period of periods) {
    const monthKey = toMonthKey(period.period.start);
    if (!seen.has(monthKey)) {
      const monthStartDate = startOfMonth(parseISO(period.period.start));
      seen.set(monthKey, {
        id: monthKey,
        monthStart: format(monthStartDate, ISO_DATE_FORMAT),
        monthLabel: monthKey,
      });
    }
  }
  return Array.from(seen.values()).sort((left, right) =>
    left.id.localeCompare(right.id),
  );
};

const getServiceRange = (
  servicePeriodIndex: Map<string, ServicePeriodRange>,
  periodKey: string,
) => servicePeriodIndex.get(periodKey);

const buildRowsFromSubscriptionItems = (
  subscriptionItems: IRevenueScheduleSubscriptionItem[],
  servicePeriodIndex: Map<string, ServicePeriodRange>,
): RowData[] =>
  subscriptionItems.map((item) => {
    const range = getServiceRange(servicePeriodIndex, item.subscriptionItemId);
    return {
      rowId: item.subscriptionItemId,
      periodKey: item.subscriptionItemId,
      offeringId: item.references.offeringId ?? '',
      offeringName: item.offeringName ?? '',
      productId: item.references.productId ?? '',
      productName: item.productName ?? '',
      productType: item.productType ?? '',
      recognitionMethod: item.recognitionMethod ?? '',
      recognitionDuration: item.recognitionDuration,
      serviceStart: range?.start ?? '',
      serviceEnd: range?.end ?? '',
      productAmount: item.productAmount ?? 0,
    };
  });

const buildRowsFromPeriods = (
  periods: IRevenueSchedulePeriod[],
  servicePeriodIndex: Map<string, ServicePeriodRange>,
): RowData[] => {
  const map = new Map<string, RowData>();
  for (const period of periods) {
    const periodKey = getPeriodGroupKey(period);
    if (!map.has(periodKey)) {
      const range = getServiceRange(servicePeriodIndex, periodKey);
      map.set(periodKey, {
        rowId: periodKey,
        periodKey,
        offeringId: period.references.offeringId ?? '',
        offeringName: period.offeringName ?? '',
        productId: period.references.productId ?? '',
        productName: period.productName ?? '',
        productType: period.productType ?? '',
        recognitionMethod: period.recognitionMethod ?? '',
        recognitionDuration: period.recognitionDuration,
        serviceStart: range?.start ?? '',
        serviceEnd: range?.end ?? '',
        productAmount: period.productAmount,
      });
    }
  }
  return Array.from(map.values());
};

type PeriodIndex = Map<string, IRevenueSchedulePeriod>;

const periodIndexKey = (periodKey: string, monthKey: string) =>
  `${periodKey}|${monthKey}`;

const buildPeriodIndex = (periods: IRevenueSchedulePeriod[]): PeriodIndex => {
  const index: PeriodIndex = new Map();
  for (const period of periods) {
    const monthKey = toMonthKey(period.period.start);
    index.set(periodIndexKey(getPeriodGroupKey(period), monthKey), period);
  }
  return index;
};

const isMonthLoaded = (
  monthStart: string,
  loadedThrough: string | null | undefined,
) => {
  if (loadedThrough == null) {
    return true;
  }
  return monthStart <= loadedThrough;
};

const roundForCompare = (value: number) => Math.round(value * 1e8) / 1e8;

const computeRowTotals = (
  row: RowData,
  monthColumns: MonthColumn[],
  periodIndex: PeriodIndex,
  editedRevenues: Record<string, number>,
) => {
  let newTotal = 0;
  for (const column of monthColumns) {
    const period = periodIndex.get(periodIndexKey(row.periodKey, column.id));
    if (!period) {
      continue;
    }
    newTotal += editedRevenues[period.id] ?? period.revenue;
  }
  return {
    oldTotal: roundForCompare(row.productAmount),
    newTotal: roundForCompare(newTotal),
  };
};

const computeGrandTotals = (
  dataRows: RowData[],
  monthColumns: MonthColumn[],
  periodIndex: PeriodIndex,
  editedRevenues: Record<string, number>,
) => {
  let oldTotal = 0;
  let newTotal = 0;
  for (const row of dataRows) {
    const totals = computeRowTotals(
      row,
      monthColumns,
      periodIndex,
      editedRevenues,
    );
    oldTotal += row.productAmount;
    newTotal += totals.newTotal;
  }
  return {
    oldTotal: roundForCompare(oldTotal),
    newTotal: roundForCompare(newTotal),
  };
};

const renderChangeableAmount = (
  oldTotal: number,
  newTotal: number,
  isEditable: boolean,
  trailingNode?: ReactNode,
) => {
  const hasChange = isEditable && newTotal !== oldTotal;
  const trailing = trailingNode ? <MBox ml={1}>{trailingNode}</MBox> : null;
  if (hasChange) {
    return (
      <MBox>
        <MText
          fontSize="xs"
          color="tGray.darkPurple"
          textDecoration="line-through"
        >
          {formatCurrency(oldTotal)}
        </MText>
        <MFlex alignItems="center">
          <MText fontWeight="bold" fontSize="xs">
            {formatCurrency(newTotal)}
          </MText>
          {trailing}
        </MFlex>
      </MBox>
    );
  }
  return (
    <MFlex alignItems="center">
      <MText fontWeight="bold" fontSize="xs">
        {formatCurrency(oldTotal)}
      </MText>
      {trailing}
    </MFlex>
  );
};

interface BuildColumnsParams {
  monthColumns: MonthColumn[];
  dataRows: RowData[];
  periodIndex: PeriodIndex;
  contractAmount: number | null | undefined;
  isEditable: boolean;
  editedRevenuesRef: MutableRefObject<Record<string, number>>;
  setEditedRevenue: (periodId: string, value: number) => void;
  loadedThrough: string | null | undefined;
  isFullyLoaded: boolean;
  todayIso: string;
  activeLock: IAccountingPeriodLock | null | undefined;
}

const buildColumns = ({
  monthColumns,
  dataRows,
  periodIndex,
  contractAmount,
  isEditable,
  editedRevenuesRef,
  setEditedRevenue,
  loadedThrough,
  isFullyLoaded,
  todayIso,
  activeLock,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: BuildColumnsParams): AppColumnDef<RowData, any>[] => {
  return [
    columnHelper.accessor('offeringName', {
      header: 'Offering',
      size: NARROW_COLUMN_WIDTH,
      enableSorting: false,
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          return (
            <MText fontWeight="bold" fontSize="xs">
              Total
            </MText>
          );
        }
        const { offeringId, offeringName } = info.row.original;
        const label = offeringName || offeringId || '—';
        if (!offeringId) {
          return <MText fontSize="xs">{label}</MText>;
        }
        return (
          <OfferingLookupPopover
            offeringId={offeringId}
            label={label}
            hideEditLink
          />
        );
      },
    }),
    columnHelper.accessor('productName', {
      header: 'Product',
      size: 170,
      enableSorting: false,
      meta: { cellStyles: COLUMN_DIVIDER_STYLES },
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          return null;
        }
        const { productId, productName } = info.row.original;
        const label = productName || productId || '—';
        if (!productId) {
          return <MText fontSize="xs">{label}</MText>;
        }
        return (
          <ProductLookupPopover
            productId={productId}
            label={label}
            hideEditLink
          />
        );
      },
    }),
    columnHelper.accessor('productType', {
      header: 'Product Type',
      size: NARROW_COLUMN_WIDTH,
      enableSorting: false,
      meta: { cellStyles: COLUMN_DIVIDER_STYLES },
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          return null;
        }
        return (
          <MText fontSize="xs">
            {formatProductType(info.row.original.productType)}
          </MText>
        );
      },
    }),
    columnHelper.display({
      id: 'recognitionMethod',
      header: 'Recognition Method',
      size: 180,
      enableSorting: false,
      meta: { cellStyles: COLUMN_DIVIDER_STYLES },
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          return null;
        }
        const { recognitionMethod, recognitionDuration } = info.row.original;
        return (
          <MText fontSize="xs">
            {formatRecognitionMethod(recognitionMethod, recognitionDuration)}
          </MText>
        );
      },
    }),
    columnHelper.display({
      id: 'servicePeriod',
      header: 'Service Period',
      size: 200,
      enableSorting: false,
      meta: { cellStyles: COLUMN_DIVIDER_STYLES },
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          return null;
        }
        if (!isFullyLoaded) {
          return <MSkeleton height="16px" width="80%" />;
        }
        const { serviceStart, serviceEnd } = info.row.original;
        return (
          <MText fontSize="xs">
            {formatServicePeriod({ start: serviceStart, end: serviceEnd })}
          </MText>
        );
      },
    }),
    ...monthColumns.map((monthColumn) => {
      const isColumnLocked = isPeriodColumnLocked(
        monthColumn.monthStart,
        activeLock,
      );
      return columnHelper.display({
        id: monthColumn.id,
        header: () => (
          <MFlex alignItems="center" gap={1}>
            <MText fontWeight="semibold" fontSize="xs">
              {monthColumn.monthLabel}
            </MText>
            {isColumnLocked && (
              <MTooltip label={LOCKED_PERIOD_TOOLTIP} hasArrow>
                <MBox as="span" display="inline-flex" alignItems="center">
                  <MIcon as={MdLock} boxSize={3} color="tGray.darkPurple" />
                </MBox>
              </MTooltip>
            )}
          </MFlex>
        ),
        size: COLUMN_WIDTH,
        enableSorting: false,
        meta: { cellStyles: MONTH_CELL_STYLES },
        cell: (info: AppCellContext<RowData, unknown>) => {
          if (info.row.original.isTotalRow) {
            return null;
          }

          const monthLoaded = isMonthLoaded(
            monthColumn.monthStart,
            loadedThrough,
          );
          if (!monthLoaded) {
            return <MSkeleton height="16px" width="80%" />;
          }

          const period = periodIndex.get(
            periodIndexKey(info.row.original.periodKey, monthColumn.id),
          );
          if (!period) {
            return '—';
          }

          const editedValue = editedRevenuesRef.current[period.id];
          const currentValue = editedValue ?? period.revenue;

          const isUserOverride =
            period.references?.sourceId?.type ===
            RevenueScheduleSourceTypeEnum.USER;

          const isLocked = isPeriodRecognized(
            period.period.endInclusive,
            todayIso,
          );
          const highlightCell = !isEditable && isUserOverride;

          const cellBackground = highlightCell ? (
            <MBox
              position="absolute"
              inset={0}
              bg="tOrange.input"
              pointerEvents="none"
            />
          ) : null;

          if (
            isEditable &&
            activeLock !== undefined &&
            !isColumnLocked &&
            !isLocked
          ) {
            return (
              <>
                {cellBackground}
                <MBox position="relative" width="100%">
                  <MCustomNumberInput
                    size="xs"
                    inputMode="decimal"
                    min={-Infinity}
                    precision={REVENUE_INPUT_PRECISION}
                    handleChange={limitRevenueInputDecimals}
                    value={currentValue}
                    onChange={(_str, num) => setEditedRevenue(period.id, num)}
                  />
                </MBox>
              </>
            );
          }

          const valueNode = (
            <MBox position="relative">
              <PeriodAuditPopover period={period}>
                <MText fontSize="xs">{formatCurrency(currentValue)}</MText>
              </PeriodAuditPopover>
            </MBox>
          );
          return (
            <>
              {cellBackground}
              {isEditable && isLocked ? (
                <MTooltip label={LOCKED_PERIOD_MESSAGE} hasArrow>
                  {valueNode}
                </MTooltip>
              ) : (
                valueNode
              )}
            </>
          );
        },
      });
    }),
    columnHelper.accessor('productAmount', {
      header: 'Sales Amount',
      size: COLUMN_WIDTH,
      enableSorting: false,
      meta: { cellStyles: AMOUNT_CELL_STYLES },
      cell: (info) => {
        if (info.row.original.isTotalRow) {
          const { oldTotal, newTotal } = computeGrandTotals(
            dataRows,
            monthColumns,
            periodIndex,
            editedRevenuesRef.current,
          );
          const effectiveTotal = isEditable ? newTotal : oldTotal;
          const mismatch =
            contractAmount != null &&
            roundForCompare(effectiveTotal) !== roundForCompare(contractAmount);
          const mismatchIcon = mismatch ? (
            <MTooltip label={TCV_MISMATCH_MESSAGE} hasArrow>
              <MBox as="span" display="inline-flex" alignItems="center">
                <MIcon as={MdError} color="tRed.base" boxSize={4} />
              </MBox>
            </MTooltip>
          ) : null;
          return (
            <>
              {mismatch && (
                <MBox
                  position="absolute"
                  inset={0}
                  bg="tRed.50"
                  pointerEvents="none"
                />
              )}
              <MBox position="relative" width="100%">
                {renderChangeableAmount(
                  oldTotal,
                  newTotal,
                  isEditable,
                  mismatchIcon,
                )}
              </MBox>
            </>
          );
        }
        const { oldTotal, newTotal } = computeRowTotals(
          info.row.original,
          monthColumns,
          periodIndex,
          editedRevenuesRef.current,
        );
        const rowMismatch =
          isFullyLoaded &&
          roundForCompare(oldTotal) !== roundForCompare(newTotal);
        return (
          <>
            {rowMismatch && (
              <MBox
                position="absolute"
                inset={0}
                bg="tRed.50"
                pointerEvents="none"
              />
            )}
            <MBox position="relative" width="100%">
              {renderChangeableAmount(oldTotal, newTotal, isEditable)}
            </MBox>
          </>
        );
      },
    }),
  ];
};

export const RevenueScheduleTable: FC<RevenueScheduleTableProps> = ({
  subscriptionItems,
  periods,
  contractStart,
  contractEndInclusive,
  loadedThrough,
  isEditable = false,
  contractAmount,
  activeLock,
  isLoadingMore = false,
  onChangeEditedRevenues,
  onLoadMore,
}) => {
  const servicePeriodIndex = useMemo(
    () => buildServicePeriodIndex(periods),
    [periods],
  );

  const dataRows = useMemo<RowData[]>(() => {
    if (subscriptionItems.length > 0) {
      return buildRowsFromSubscriptionItems(
        subscriptionItems,
        servicePeriodIndex,
      );
    }
    return buildRowsFromPeriods(periods, servicePeriodIndex);
  }, [subscriptionItems, periods, servicePeriodIndex]);

  const monthColumns = useMemo<MonthColumn[]>(() => {
    if (contractStart && contractEndInclusive) {
      return buildMonthColumnsFromContract(contractStart, contractEndInclusive);
    }
    return buildMonthColumnsFromPeriods(periods);
  }, [contractStart, contractEndInclusive, periods]);

  const periodIndex = useMemo(() => buildPeriodIndex(periods), [periods]);

  const rows = useMemo<RowData[]>(() => {
    if (!dataRows.length) {
      return dataRows;
    }
    const totalRow: RowData = {
      rowId: TOTAL_ROW_ID,
      periodKey: '',
      offeringId: '',
      offeringName: '',
      productId: '',
      productName: '',
      productType: '',
      recognitionMethod: '',
      recognitionDuration: null,
      serviceStart: '',
      serviceEnd: '',
      productAmount: 0,
      isTotalRow: true,
    };
    return [...dataRows, totalRow];
  }, [dataRows]);

  const [editedRevenues, setEditedRevenues] = useState<Record<string, number>>(
    {},
  );

  const editedRevenuesRef = useRef(editedRevenues);
  editedRevenuesRef.current = editedRevenues;

  useEffect(() => {
    if (!isEditable) {
      setEditedRevenues({});
    }
  }, [isEditable]);

  useEffect(() => {
    if (!activeLock) {
      return;
    }
    setEditedRevenues((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev;
      }
      const filtered = filterLockedEdits(prev, periods, activeLock);
      return Object.keys(filtered).length === Object.keys(prev).length
        ? prev
        : filtered;
    });
  }, [activeLock, periods]);

  useEffect(() => {
    onChangeEditedRevenues?.(editedRevenues);
  }, [editedRevenues, onChangeEditedRevenues]);

  const setEditedRevenue = useCallback((periodId: string, value: number) => {
    setEditedRevenues((prev) => ({ ...prev, [periodId]: value }));
  }, []);

  const isFullyLoaded = useMemo(() => {
    if (loadedThrough == null) {
      return true;
    }
    if (monthColumns.length === 0) {
      return true;
    }
    return loadedThrough >= monthColumns[monthColumns.length - 1].monthStart;
  }, [loadedThrough, monthColumns]);

  const todayIso = useMemo(() => format(new Date(), ISO_DATE_FORMAT), []);

  const columns = useMemo(
    () =>
      buildColumns({
        monthColumns,
        dataRows,
        periodIndex,
        contractAmount,
        isEditable,
        editedRevenuesRef,
        setEditedRevenue,
        loadedThrough,
        isFullyLoaded,
        todayIso,
        activeLock,
      }),
    [
      monthColumns,
      dataRows,
      periodIndex,
      contractAmount,
      isEditable,
      setEditedRevenue,
      loadedThrough,
      isFullyLoaded,
      todayIso,
      activeLock,
    ],
  );

  const loadMoreRef = useRef<{
    onLoadMore: (() => void) | undefined;
    isLoadingMore: boolean;
    armed: boolean;
  }>({ onLoadMore, isLoadingMore, armed: true });
  loadMoreRef.current.onLoadMore = onLoadMore;
  loadMoreRef.current.isLoadingMore = isLoadingMore;

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreRef.current.armed = true;
    }
  }, [isLoadingMore]);

  const handleTableContainerScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const {
        onLoadMore: handler,
        isLoadingMore: loadingMore,
        armed,
      } = loadMoreRef.current;
      if (!handler || loadingMore || !armed) {
        return;
      }
      const el = event.currentTarget;
      if (el.scrollLeft <= 0) {
        return;
      }
      loadMoreRef.current.armed = false;
      handler();
    },
    [],
  );

  const outerContainerProps = useMemo(
    () => ({
      maxHeight: 'none',
      pb: 0,
      onScroll: handleTableContainerScroll,
    }),
    [handleTableContainerScroll],
  );

  const extraProps = useMemo(
    () => ({
      containerProps: { onScroll: handleTableContainerScroll },
    }),
    [handleTableContainerScroll],
  );

  if (!dataRows.length) {
    return (
      <MBox py={4}>
        <MText color="tGray.darkPurple" fontSize="sm">
          No revenue periods found.
        </MText>
      </MBox>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={rows}
      enablePagination={false}
      clientControlled
      skipFilterValuePreprocessing
      pinnedLeftColumns={PINNED_LEFT_COLUMNS}
      pinnedRightColumns={PINNED_RIGHT_COLUMNS}
      tableProps={{
        bodyRow: {
          cursor: 'default',
          _hover: { backgroundColor: 'inherit' },
        },
      }}
      containerProps={outerContainerProps}
      extraProps={extraProps}
      getRowId={(row) => (row as RowData).rowId}
    />
  );
};
